// Editor v5 (Jul 17) — a full-screen, Figma/Canva-style editor PROTOTYPE at
// `/editor-v5/:id`. Isolated from the existing editors (StudioFinishEditor /
// OverlayEditor / StepText stay untouched). It REUSES the proven primitives —
// composeRender (drag math, layer style, flatten, stacking), MarqueeOverlay,
// and the gallery/api lib — but wraps them in a new 100vh, no-page-scroll shell:
//
//   ┌──────────────── top bar (title · version · Save/Download) ──────────────┐
//   │  [layers rail] │  centered auto-fit canvas + floating AI bar │ [props]  │
//   └────────────────┴─────────────────────────────────────────────┴─────────┘
//
// Styling follows the APP design system (white surfaces, primary pink, surface
// tokens) — same tokens as StudioFinishEditor/OverlayEditor. The AI + Marquee +
// Re-render controls live in a STATIONARY floating toolbar over the canvas (not
// the property rail) so growing the layer properties never buries them.
//
// Data model = Model A (overlay_json + logo_overlay_json), so a v4-saved
// creative round-trips in the existing editor and vice-versa.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MarqueeOverlay from '@/components/feature/MarqueeOverlay';
import {
  getPost,
  saveCreativeVersion,
  marqueeEditPreview,
  listCreativeVersions,
  deletePost,
  type Post,
} from '@/lib/gallery';
import { wizardEditImage, getBrand } from '@/lib/api';
import {
  aspectClass,
  aspectToRefHeight,
  clampAnchor,
  flattenComposedFrame,
  getTextLayerStyle,
  resolveStackedLayers,
  snapForPosition,
  REFERENCE_CANVAS_WIDTH,
  OVERLAY_PAD_FRAC,
  type LogoOverlay,
  type LogoTint,
  type TextOverlay,
} from '@/pages/app/lib/composeRender';
import { useCredits } from '@/hooks/useCredits';
import { useConfirm } from '@/hooks/useConfirm';
import PostToSocialModal from '@/pages/gallery/components/PostToSocialModal';

type ProtoAspect = '1:1' | '4:5' | '2:3' | '9:16' | '16:9' | '1.91:1';
const ASPECT_W_OVER_H: Record<ProtoAspect, number> = {
  '1:1': 1, '4:5': 4 / 5, '2:3': 2 / 3, '9:16': 9 / 16, '16:9': 16 / 9, '1.91:1': 1.91,
};

// Floating side panels — wide monitors only (≥ FLOAT_MIN). Below that we fall
// back to docked rails. In float mode the artboard is HEIGHT-driven (fills the
// available vertical space) and the two cards float beside it, hugging its edges.
const FLOAT_MIN = 1500;         // ≥ this viewport width → floating cards
const PANEL_GAP = 16;           // gap between the artboard and each floating card
const FLOAT_LEFT_W = 224;       // left tools card width
const FLOAT_RIGHT_W = 372;      // right (layer names | properties) card width
const FLOAT_TOP = 24;           // gap below the top bar
const FLOAT_VERT_RESERVE = 56;  // top + bottom breathing room
// Reserve = 2 × (widest card + 2×gap) so the centred artboard leaves room for both.
const FLOAT_SIDE_RESERVE = 2 * (FLOAT_RIGHT_W + 2 * PANEL_GAP);

const FONT_OPTIONS = [
  'Manrope', 'Inter', 'DM Sans', 'Archivo', 'Space Grotesk', 'Fraunces',
  'Cormorant Garamond', 'Bodoni Moda', 'Playfair Display', 'Instrument Serif',
  'Bebas Neue', 'Anton', 'Chivo', 'Work Sans', 'Syne', 'Poppins', 'Lora',
  'Montserrat', 'Lato', 'Raleway', 'Merriweather', 'Roboto', 'Open Sans',
];
const WEIGHT_OPTIONS: { v: TextOverlay['fontWeight']; l: string }[] = [
  { v: 'normal', l: 'Reg' }, { v: 'semibold', l: 'Semi' }, { v: 'bold', l: 'Bold' },
];
const CASE_OPTIONS: { v: TextOverlay['textTransform']; l: string }[] = [
  { v: 'none', l: 'Aa' }, { v: 'uppercase', l: 'AA' }, { v: 'lowercase', l: 'aa' },
];
const ALIGN_OPTIONS: { v: TextOverlay['textAlign']; icon: string }[] = [
  { v: 'left', icon: 'ri-align-left' }, { v: 'center', icon: 'ri-align-center' }, { v: 'right', icon: 'ri-align-right' },
];
const PLACEMENT_CELLS = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'middle-center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
];
const BW_SWATCHES = ['#FFFFFF', '#000000'];

// ── App-tokened primitives ───────────────────────────────────────────────────
const chip = (active: boolean) =>
  `h-8 min-w-8 px-2 inline-flex items-center justify-center rounded-md text-xs font-bold cursor-pointer transition-colors border ${
    active ? 'bg-primary-light text-primary border-primary' : 'bg-white text-dark border-surface-dark/40 hover:border-primary/40'
  }`;
const sectionLabel = 'text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted';

export default function EditorV5Page() {
  const { id: postId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { refresh: refreshCredits } = useCredits();
  const confirm = useConfirm();

  // ── Post + editor state ────────────────────────────────────────────────
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<Post[]>([]);
  const [versionsOpen, setVersionsOpen] = useState(false);

  const [frame, setFrame] = useState<string>('');
  const [overlays, setOverlays] = useState<TextOverlay[]>([]);
  const [logoOverlays, setLogoOverlays] = useState<LogoOverlay[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [retryQ, setRetryQ] = useState<'low' | 'medium' | 'high' | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [marqueeMode, setMarqueeMode] = useState(false);
  const [marqueeApplying, setMarqueeApplying] = useState(false);
  const [marqueePreview, setMarqueePreview] = useState<
    { originalUrl: string; dirtyBefore: boolean } | null
  >(null);

  const [mobileTab, setMobileTab] = useState<'tools' | 'layers' | 'props'>('tools');
  const [publishOpen, setPublishOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [fullscreenUrl, setFullscreenUrl] = useState<string | null>(null);

  const stageRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [previewW, setPreviewW] = useState(0);
  const [viewportW, setViewportW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));

  const overlaysRef = useRef(overlays); overlaysRef.current = overlays;
  const logoOverlaysRef = useRef(logoOverlays); logoOverlaysRef.current = logoOverlays;
  const activeIdRef = useRef(activeId); activeIdRef.current = activeId;
  const draggingLogoRef = useRef(false);
  const dragBoxRef = useRef<{ w: number; h: number } | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);

  const aspect = (['1:1', '4:5', '2:3', '9:16', '16:9', '1.91:1'].includes(post?.aspect_ratio ?? '')
    ? post?.aspect_ratio : '1:1') as ProtoAspect;

  // ── Load post + hydrate Model-A overlays ───────────────────────────────
  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPost(postId)
      .then((p) => {
        if (cancelled) return;
        setPost(p);
        setFrame(p.base_image_url || p.image_url || '');
        setOverlays(Array.isArray(p.overlay_json) ? (p.overlay_json as TextOverlay[]) : []);
        setLogoOverlays(Array.isArray(p.logo_overlay_json) ? (p.logo_overlay_json as LogoOverlay[]) : []);
        setActiveId(null);
        setDirty(false);
        setMarqueePreview(null);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load this creative.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [postId]);

  useEffect(() => {
    getBrand().then((b) => setBrandLogoUrl(b?.logoUrl ?? null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!postId) return;
    listCreativeVersions(postId).then(setVersions).catch(() => setVersions([]));
  }, [postId]);

  // ── Canvas fit ─────────────────────────────────────────────────────────
  // Keyed on `loading` (NOT []): the <main> stage only exists AFTER loading
  // flips false, so a mount-only effect would measure nothing (stageRef.current
  // is null during the loading gate) and boxW would stay 0 → blank canvas.
  useEffect(() => {
    if (loading) return;
    const el = stageRef.current;
    if (!el) return;
    const update = () => { const r = el.getBoundingClientRect(); setStageSize({ w: r.width, h: r.height }); };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setPreviewW(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageSize]);
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const floatMode = viewportW >= FLOAT_MIN;
  const wOverH = ASPECT_W_OVER_H[aspect] ?? 1;
  // Float mode: <main> is full width (cards are absolute/out-of-flow) — drive the
  // artboard by HEIGHT (fills the vertical space) and reserve side room for the
  // cards so they hug its edges. Docked mode: fit to the narrowed flex column.
  const boxW = stageSize.w > 0 && stageSize.h > 0
    ? (floatMode
        ? Math.floor(Math.min((stageSize.h - FLOAT_VERT_RESERVE) * wOverH, stageSize.w - FLOAT_SIDE_RESERVE))
        : Math.floor(Math.min(stageSize.w, stageSize.h * wOverH)))
    : 0;
  const boxH = boxW > 0 ? Math.round(boxW / wOverH) : 0; // artboard height → cards match it
  const previewFontScale = previewW > 0 ? previewW / REFERENCE_CANVAS_WIDTH : 1;

  // ── Drag-to-position (canonical handler) ───────────────────────────────
  useEffect(() => {
    if (!draggingId) return;
    const move = (clientX: number, clientY: number) => {
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;
      const cx = (clientX - rect.left) / rect.width;
      const cy = (clientY - rect.top) / rect.height;
      const off = dragOffsetRef.current;
      const tx = off ? cx - off.dx : cx;
      const ty = off ? cy - off.dy : cy;
      const box = dragBoxRef.current;
      if (draggingLogoRef.current) {
        const logo = logoOverlaysRef.current.find((l) => l.id === draggingId);
        if (!logo) return;
        const wFrac = box ? box.w / rect.width : logo.widthFrac;
        const hFrac = box ? box.h / rect.height : logo.widthFrac;
        setLogoOverlays(logoOverlaysRef.current.map((l) =>
          l.id === draggingId
            ? { ...l, x: Math.max(0, Math.min(1 - wFrac, tx)), y: Math.max(0, Math.min(1 - hFrac, ty)), positionId: '' }
            : l));
        return;
      }
      const dragged = overlaysRef.current.find((o) => o.id === draggingId);
      if (!dragged) return;
      const wFrac = box ? box.w / rect.width : 0;
      const hFrac = box ? box.h / rect.height : 0;
      const { x, y } = clampAnchor(tx, ty, wFrac, hFrac, dragged.anchor);
      setOverlays(overlaysRef.current.map((o) => o.id === draggingId ? { ...o, x, y, positionId: '' } : o));
    };
    const onMouse = (e: MouseEvent) => move(e.clientX, e.clientY);
    const onTouch = (e: TouchEvent) => { const t = e.touches[0]; if (t) move(t.clientX, t.clientY); };
    const onUp = () => { setDraggingId(null); dragBoxRef.current = null; dragOffsetRef.current = null; draggingLogoRef.current = false; };
    window.addEventListener('mousemove', onMouse);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId]);

  // ── Derived ────────────────────────────────────────────────────────────
  const ctaLayer = overlays.find((o) => o.id.startsWith('cta-')) || null;
  const textLayers = overlays.filter((o) => !o.id.startsWith('cta-'));
  const logoLayer = logoOverlays[0] || null;
  const refH = aspectToRefHeight(aspect);
  const resolved = useMemo(
    () => resolveStackedLayers(overlays, REFERENCE_CANVAS_WIDTH, refH) as TextOverlay[],
    [overlays, refH],
  );
  const activeOverlay = overlays.find((o) => o.id === activeId) || null;
  const activeLogo = logoOverlays.find((l) => l.id === activeId) || null;

  const renderBusy = retryQ !== null || aiBusy || marqueeApplying;
  const mutating = renderBusy || marqueePreview !== null;
  const busyLabel = retryQ ? `Re-rendering (${retryQ})…` : aiBusy ? 'Applying your edit…' : marqueeApplying ? 'Editing region…' : '';

  // ── Layer mutation ─────────────────────────────────────────────────────
  const markDirty = () => setDirty(true);
  const updateOverlay = (idl: string, u: Partial<TextOverlay>) => {
    setOverlays((prev) => prev.map((o) => (o.id === idl ? { ...o, ...u } : o))); markDirty();
  };
  const updateLogo = (idl: string, u: Partial<LogoOverlay>) => {
    setLogoOverlays((prev) => prev.map((l) => (l.id === idl ? { ...l, ...u } : l))); markDirty();
  };
  const removeLayer = (idl: string) => {
    setOverlays((prev) => prev.filter((o) => o.id !== idl));
    setLogoOverlays((prev) => prev.filter((l) => l.id !== idl));
    if (activeIdRef.current === idl) setActiveId(null);
    markDirty();
  };
  const addTextLayer = () => {
    const used = new Set(textLayers.map((o) => o.text));
    const text = post?.headline && !used.has(post.headline) ? post.headline
      : post?.tagline && !used.has(post.tagline) ? post.tagline : 'Your text here';
    const next: TextOverlay = {
      kind: 'text', id: `text-${Date.now()}`, imageIndex: 0, text,
      x: 0.5, y: 0.4, anchor: 'mc', maxWidthFrac: 0.82, textAlign: 'center',
      fontSize: 64, fontWeight: 'bold', fontFamily: 'Manrope', color: '#FFFFFF',
      textTransform: 'uppercase', maxLines: 3,
    };
    setOverlays((prev) => [...prev, next]); setActiveId(next.id); markDirty();
  };
  const addCtaLayer = () => {
    if (ctaLayer) { setActiveId(ctaLayer.id); return; }
    const next: TextOverlay = {
      kind: 'text', id: `cta-${Date.now()}`, imageIndex: 0, text: 'SHOP NOW',
      x: 0.5, y: 0.84, anchor: 'mc', maxWidthFrac: 0.6, textAlign: 'center',
      fontSize: 34, fontWeight: 'bold', fontFamily: 'Manrope', color: '#FFFFFF',
      textTransform: 'uppercase', maxLines: 1, backgroundColor: '#0F0F1E', backgroundPadding: 18,
    };
    setOverlays((prev) => [...prev, next]); setActiveId(next.id); markDirty();
  };
  const addLogoLayer = () => {
    if (logoLayer) { setActiveId(logoLayer.id); return; }
    if (!brandLogoUrl) return;
    const w = 0.18;
    const next: LogoOverlay = {
      id: `logo-${Date.now()}`, imageIndex: 0, src: brandLogoUrl,
      x: 1 - w - 0.04, y: 1 - w - 0.04, widthFrac: w, opacity: 1, tint: 'original',
    };
    setLogoOverlays((prev) => [...prev, next]); setActiveId(next.id); markDirty();
  };
  const placeTextAt = (posId: string) => { if (activeOverlay) updateOverlay(activeOverlay.id, snapForPosition(posId)); };
  const placeLogoAt = (posId: string) => {
    const logo = logoOverlaysRef.current.find((l) => l.id === activeIdRef.current);
    const preview = previewRef.current;
    if (!logo || !preview) return;
    const pr = preview.getBoundingClientRect();
    if (pr.width === 0 || pr.height === 0) return;
    const el = preview.querySelector(`[data-logo-id="${logo.id}"]`) as HTMLElement | null;
    if (!el) return;
    const lr = el.getBoundingClientRect();
    const wFrac = lr.width / pr.width, hFrac = lr.height / pr.height;
    const pad = OVERLAY_PAD_FRAC;
    const left = posId.includes('left'), right = posId.includes('right');
    const top = posId.startsWith('top'), bottom = posId.startsWith('bottom');
    const x = left ? pad : right ? 1 - pad - wFrac : 0.5 - wFrac / 2;
    const y = top ? pad : bottom ? 1 - pad - hFrac : 0.5 - hFrac / 2;
    updateLogo(logo.id, { x: Math.max(0, Math.min(1 - wFrac, x)), y: Math.max(0, Math.min(1 - hFrac, y)), positionId: posId });
  };
  const logoFilter = (t: LogoTint | undefined) =>
    t === 'black' ? 'brightness(0)' : t === 'white' ? 'brightness(0) invert(1)' : undefined;

  // ── Save / AI / re-render / marquee / download ─────────────────────────
  const doSave = useCallback(async (): Promise<Post | null> => {
    if (!post?.id || !frame) return null;
    if (!dirty) return post;
    setSaving(true);
    const urls: string[] = [];
    try {
      const src = await (await fetch(frame)).blob();
      let outputBlob = src;
      if (overlays.length > 0 || logoOverlays.length > 0) {
        const objUrl = URL.createObjectURL(src); urls.push(objUrl);
        const flat = await flattenComposedFrame({ imageSrc: objUrl, frameIndex: 0, overlays, shapeOverlays: [], logoOverlays });
        if (!flat) throw new Error('Could not flatten the image.');
        outputBlob = flat;
      }
      const newPost = await saveCreativeVersion({
        sourcePostId: post.id, blob: outputBlob, baseBlob: src,
        overlayJson: overlays, logoOverlayJson: logoOverlays,
      });
      setDirty(false);
      return newPost;
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Save failed');
      return null;
    } finally {
      for (const u of urls) URL.revokeObjectURL(u);
      setSaving(false);
    }
  }, [post, frame, dirty, overlays, logoOverlays]);

  const handleSave = useCallback(async () => {
    const np = await doSave();
    if (np && np.id !== postId) navigate(`/editor-v5/${np.id}`, { replace: true });
  }, [doSave, navigate, postId]);

  const handleAiEdit = useCallback(async () => {
    const prompt = aiPrompt.trim();
    if (!prompt || !frame) return;
    setAiBusy(true); setOpError(null);
    try {
      const blob = await (await fetch(frame)).blob();
      const r = await wizardEditImage({ image: blob, prompt, aspectRatio: post?.aspect_ratio ?? '1:1', compositeLogo: false });
      setFrame(`data:image/png;base64,${r.image_b64}`);
      setAiPrompt(''); setDirty(true); void refreshCredits();
    } catch (e) { setOpError(e instanceof Error ? e.message : 'Edit failed'); }
    finally { setAiBusy(false); }
  }, [aiPrompt, frame, post?.aspect_ratio, refreshCredits]);

  const handleReRender = useCallback(async (q: 'low' | 'medium' | 'high') => {
    if (!frame || retryQ) return;
    setRetryQ(q); setOpError(null);
    try {
      const blob = await (await fetch(frame)).blob();
      const r = await wizardEditImage({
        image: blob,
        prompt: 'Re-render this advertising image with enhanced clarity, sharper detail, and refined typography. Preserve the composition, subject, layout, and palette exactly — only improve fidelity.',
        aspectRatio: post?.aspect_ratio ?? '1:1', compositeLogo: false, quality: q,
      });
      setFrame(`data:image/png;base64,${r.image_b64}`);
      setDirty(true); void refreshCredits();
    } catch (e) { setOpError(e instanceof Error ? e.message : 'Re-render failed'); }
    finally { setRetryQ(null); }
  }, [frame, retryQ, post?.aspect_ratio, refreshCredits]);

  const handleMarqueeApply = useCallback(async (
    rect: { x: number; y: number; w: number; h: number }, instruction: string,
  ) => {
    if (!post || marqueeApplying || marqueePreview) return;
    const originalUrl = frame; const dirtyBefore = dirty;
    setMarqueeApplying(true); setOpError(null);
    try {
      const { previewUrl } = await marqueeEditPreview(post.id, rect, instruction);
      setFrame(previewUrl); setDirty(true); setMarqueeMode(false);
      setMarqueePreview({ originalUrl, dirtyBefore }); void refreshCredits();
    } catch (e) { setOpError(e instanceof Error ? e.message : 'Region edit failed'); }
    finally { setMarqueeApplying(false); }
  }, [post, marqueeApplying, marqueePreview, frame, dirty, refreshCredits]);

  const handleMarqueeKeep = useCallback(async () => {
    if (!marqueePreview) return;
    const np = await doSave();
    if (np && np.id !== postId) navigate(`/editor-v5/${np.id}`, { replace: true });
    else setMarqueePreview(null);
  }, [marqueePreview, doSave, navigate, postId]);

  const handleMarqueeDiscard = useCallback(() => {
    if (!marqueePreview) return;
    setFrame(marqueePreview.originalUrl); setDirty(marqueePreview.dirtyBefore);
    setMarqueePreview(null); setOpError(null);
  }, [marqueePreview]);

  const handleDownload = useCallback(async () => {
    if (!frame) return;
    setDownloading(true);
    const urls: string[] = [];
    try {
      const src = await (await fetch(frame)).blob();
      let out = src;
      if (overlays.length > 0 || logoOverlays.length > 0) {
        const objUrl = URL.createObjectURL(src); urls.push(objUrl);
        const flat = await flattenComposedFrame({ imageSrc: objUrl, frameIndex: 0, overlays, shapeOverlays: [], logoOverlays });
        if (flat) out = flat;
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(out); urls.push(a.href);
      a.download = `${(post?.title || 'creative').replace(/[^a-z0-9]+/gi, '-')}.png`;
      a.click();
    } finally {
      setTimeout(() => urls.forEach((u) => URL.revokeObjectURL(u)), 4000);
      setDownloading(false);
    }
  }, [frame, overlays, logoOverlays, post?.title]);

  const handleDelete = useCallback(async () => {
    if (!post) return;
    const ok = await confirm({
      title: 'Delete this creative?',
      message: 'This will permanently remove it from My Creatives. Already-published posts on Facebook / Instagram remain live.',
      destructive: true, confirmLabel: 'Delete',
    });
    if (!ok) return;
    try { await deletePost(post.id); navigate('/gallery'); }
    catch (e) { setOpError(e instanceof Error ? e.message : 'Delete failed'); }
  }, [post, confirm, navigate]);

  // Post — publish the SAVED image. If there are unsaved edits, save first
  // (forks a version + navigates); the user hits Post again on the saved one.
  const handlePost = useCallback(async () => {
    if (!post) return;
    if (dirty) { await handleSave(); return; }
    setPublishOpen(true);
  }, [post, dirty, handleSave]);

  // Edit Baked Text — placeholder for now. Lifting baked-in text produces the
  // JSON-template (Model B) overlay the v4 canvas can't render yet; wired once
  // the editor-model unification lands.
  const handleEditBakedText = useCallback(() => {
    setToast('Edit baked text is coming soon.');
  }, []);

  // Fullscreen preview — flatten the current frame + overlays and show it in a
  // lightbox so the user can eyeball the final composite at full size.
  const handleFullscreen = useCallback(async () => {
    if (!frame) return;
    try {
      const src = await (await fetch(frame)).blob();
      let out = src;
      if (overlays.length > 0 || logoOverlays.length > 0) {
        const objUrl = URL.createObjectURL(src);
        const flat = await flattenComposedFrame({ imageSrc: objUrl, frameIndex: 0, overlays, shapeOverlays: [], logoOverlays });
        URL.revokeObjectURL(objUrl);
        if (flat) out = flat;
      }
      setFullscreenUrl(URL.createObjectURL(out));
    } catch { /* noop */ }
  }, [frame, overlays, logoOverlays]);
  const closeFullscreen = useCallback(() => {
    setFullscreenUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
  }, []);

  const handleClose = useCallback(async () => {
    if (marqueePreview) {
      const ok = await confirm({ title: 'Discard the unsaved edit?', message: "You have an edit that isn't saved. Leaving discards it — the credit is already spent. Leave anyway?", confirmLabel: 'Leave', cancelLabel: 'Stay', destructive: true });
      if (!ok) return;
    } else if (dirty) {
      const ok = await confirm({ title: 'Leave without saving?', message: 'You have unsaved changes. Leave anyway?', confirmLabel: 'Leave', cancelLabel: 'Stay', destructive: true });
      if (!ok) return;
    }
    navigate('/gallery');
  }, [marqueePreview, dirty, confirm, navigate]);

  useEffect(() => {
    if (!dirty && !marqueePreview) return;
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty, marqueePreview]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!fullscreenUrl) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeFullscreen(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreenUrl, closeFullscreen]);

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="fixed inset-0 flex items-center justify-center bg-surface text-dark-muted"><i className="ri-loader-4-line animate-spin text-2xl mr-2" /> Loading editor…</div>;
  }
  if (error || !post) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-surface text-dark">
        <p>{error || 'Creative not found.'}</p>
        <button onClick={() => navigate('/gallery')} className="px-4 h-10 rounded-full bg-primary text-white text-sm font-bold cursor-pointer">Back to My Creatives</button>
      </div>
    );
  }

  const leftTools = (
    <LeftTools
      aiPrompt={aiPrompt} setAiPrompt={setAiPrompt} onAiEdit={handleAiEdit} aiBusy={aiBusy}
      marqueeMode={marqueeMode} onToggleMarquee={() => setMarqueeMode((v) => !v)}
      retryQ={retryQ} onReRender={handleReRender} mutating={mutating} opError={opError}
      onPost={handlePost} onDownload={handleDownload} downloading={downloading}
      onSave={handleSave} saving={saving} dirty={dirty} />
  );
  const layerList = (
    <LayerList logoLayer={logoLayer} ctaLayer={ctaLayer} textLayers={textLayers}
      activeId={activeId} setActiveId={setActiveId}
      addLogoLayer={addLogoLayer} addCtaLayer={addCtaLayer} addTextLayer={addTextLayer}
      brandLogoUrl={brandLogoUrl} onEditBakedText={handleEditBakedText}
      hasBakedText={!post?.text_overlay && !!((post?.headline && post.headline.trim()) || (post?.tagline && post.tagline.trim()))} />
  );
  const propsPanel = (
    <PropertyPanel activeOverlay={activeOverlay} activeLogo={activeLogo} brandColors={[]}
      updateOverlay={updateOverlay} updateLogo={updateLogo} removeLayer={removeLayer}
      placeTextAt={placeTextAt} placeLogoAt={placeLogoAt} />
  );

  return (
    <div className="fixed inset-0 flex flex-col bg-surface select-none">
      {/* ── TOP BAR — matches /creatives:id (type + spacing) ── */}
      <header className="flex-shrink-0 bg-white border-b border-surface-dark/40">
        <div className="max-w-[1452px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={handleClose} aria-label="Back"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-dark hover:bg-surface-dark/40 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-lg" />
            <span className="hidden sm:inline text-sm font-medium">My Creatives</span>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-dark truncate">{post.title || 'Untitled creative'}</h1>
            <p className="text-xs text-dark-muted">
              {new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              {post.aspect_ratio ? ` · ${post.aspect_ratio}` : ''}
            </p>
          </div>
          {dirty && <span className="text-xs text-amber-600 font-bold flex-shrink-0">Unsaved</span>}
          {versions.length > 1 && (
            <div className="relative flex-shrink-0">
              <button onClick={() => setVersionsOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface hover:bg-surface-dark/40 text-dark text-sm font-medium cursor-pointer transition-colors">
                <i className="ri-history-line" /> v{versions.findIndex((v) => v.id === post.id) + 1 || versions.length} of {versions.length}
                <i className="ri-arrow-down-s-line" />
              </button>
              {versionsOpen && (
                <div className="absolute right-0 z-50 mt-1 w-44 rounded-xl bg-white ring-1 ring-surface-dark/40 shadow-xl py-1">
                  {versions.map((v, i) => (
                    <button key={v.id}
                      onClick={() => { setVersionsOpen(false); if (v.id !== post.id) navigate(`/editor-v5/${v.id}`, { replace: true }); }}
                      className={`w-full text-left px-3 py-2 text-sm font-medium cursor-pointer hover:bg-surface ${v.id === post.id ? 'text-primary' : 'text-dark'}`}>
                      v{i + 1}{v.is_head ? ' · latest' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button onClick={handleDelete} aria-label="Delete creative" title="Delete this creative"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50 transition-colors text-sm font-medium cursor-pointer flex-shrink-0">
            <i className="ri-delete-bin-line text-lg" />
            <span className="hidden sm:inline">Delete image</span>
          </button>
        </div>
      </header>

      {/* ── BODY: floating tools card · big canvas · floating (layers|props) card.
          Wide (≥ FLOAT_MIN): asides are floating gradient cards hugging the
          artboard edges. Narrow: docked rails. */}
      <div className="flex-1 min-h-0 relative flex">
        {/* LEFT — Describe an edit / Marquee / Re-render (top) + file actions (bottom) */}
        <aside
          className={floatMode
            ? 'hidden md:flex absolute z-30 rounded-2xl p-0.5 bg-gradient-to-br from-primary via-accent to-sky-400 shadow-[0_26px_60px_-15px_rgba(15,15,30,0.45)]'
            : 'hidden md:flex w-64 flex-shrink-0 flex-col bg-white border-r border-surface-dark/40'}
          style={floatMode ? { width: `${FLOAT_LEFT_W}px`, right: `calc(50% + ${boxW / 2 + PANEL_GAP}px)`, top: `${FLOAT_TOP}px`, height: `${boxH}px` } : undefined}>
          {floatMode ? <div className="flex flex-col w-full rounded-[14px] bg-white overflow-hidden">{leftTools}</div> : leftTools}
        </aside>

        {/* CENTER — big canvas. Float mode top-aligns it (cards hug it); docked centers. */}
        <main ref={stageRef}
          className={`relative flex-1 min-w-0 flex justify-center bg-surface-dark/30 ${floatMode ? 'items-start px-4 sm:px-8' : 'items-center p-4'}`}
          style={floatMode ? { paddingTop: `${FLOAT_TOP}px` } : undefined}
          onMouseDown={() => setActiveId(null)}>
          {boxW > 0 && (
            <div ref={previewRef}
              className={`relative rounded-2xl overflow-hidden bg-gradient-to-br from-surface to-surface-dark/40 shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] ${aspectClass(aspect)}`}
              style={{ width: `${boxW}px` }} onMouseDown={(e) => e.stopPropagation()}>
              {frame && <img src={frame} alt="Creative" className="w-full h-full object-contain" draggable={false} onMouseDown={() => setActiveId(null)} />}

              {/* Fullscreen preview */}
              {!marqueeMode && !marqueePreview && (
                <button onClick={handleFullscreen} title="View full screen"
                  className="absolute top-2.5 right-2.5 z-40 h-8 w-8 inline-flex items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm shadow-md text-dark hover:text-primary cursor-pointer">
                  <i className="ri-fullscreen-line" />
                </button>
              )}

              {renderBusy && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-dark/55 backdrop-blur-[2px]">
                  <div className="w-12 h-12 rounded-full border-[3px] border-white/30 border-t-white animate-spin" />
                  <p className="text-white text-sm font-bold drop-shadow" aria-live="polite">{busyLabel}</p>
                  <p className="text-white/80 text-xs px-6 text-center max-w-[240px]">This can take ~25s — don&apos;t leave or you&apos;ll lose the credits.</p>
                </div>
              )}

              {!marqueePreview && (
                <MarqueeOverlay enabled={marqueeMode} applying={marqueeApplying} previewRef={previewRef}
                  onApply={handleMarqueeApply} onCancel={() => setMarqueeMode(false)} />
              )}

              {/* Marquee how-to — shown while marquee is armed */}
              {marqueeMode && !marqueePreview && !marqueeApplying && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[55] px-3.5 py-2 rounded-full bg-dark/85 backdrop-blur-sm text-white text-xs font-medium shadow-lg pointer-events-none whitespace-nowrap flex items-center gap-1.5">
                  <i className="ri-cursor-line" /> Start by drawing a rectangle over the area you want to edit
                </div>
              )}

              {marqueePreview && (
                <div className="absolute inset-x-0 bottom-0 z-[60]">
                  <div className="m-3 rounded-2xl bg-dark/90 backdrop-blur-sm ring-1 ring-white/10 px-4 py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-bold leading-tight">Apply these changes?</p>
                      <p className="text-white/70 text-[11px] leading-tight mt-0.5">Keep saves it as a new version · Discard reverts.</p>
                    </div>
                    <button onClick={handleMarqueeDiscard} disabled={saving}
                      className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold cursor-pointer disabled:opacity-40">
                      <i className="ri-arrow-go-back-line" /> Discard
                    </button>
                    <button onClick={handleMarqueeKeep} disabled={saving}
                      className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold cursor-pointer disabled:opacity-60">
                      {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving…</> : <><i className="ri-check-line" /> Keep</>}
                    </button>
                  </div>
                </div>
              )}

              {resolved.map((ov) => (
                <div key={ov.id}
                  onMouseDown={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const pr = previewRef.current?.getBoundingClientRect();
                    dragBoxRef.current = { w: r.width, h: r.height };
                    if (pr && pr.width > 0 && pr.height > 0) dragOffsetRef.current = { dx: (e.clientX - pr.left) / pr.width - ov.x, dy: (e.clientY - pr.top) / pr.height - ov.y };
                    if (ov.stackOnPrevious) setOverlays(overlaysRef.current.map((o) => o.id === ov.id ? { ...o, y: ov.y, stackOnPrevious: false } : o));
                    setActiveId(ov.id); setDraggingId(ov.id);
                  }}
                  className={`absolute ${draggingId === ov.id ? 'cursor-grabbing' : 'cursor-grab'} ${activeId === ov.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''}`}
                  style={getTextLayerStyle(ov, { previewFontScale })}>
                  {ov.text}
                </div>
              ))}

              {logoOverlays.map((logo) => (
                <img key={logo.id} src={logo.src} alt="Logo" draggable={false} data-logo-id={logo.id}
                  onMouseDown={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const pr = previewRef.current?.getBoundingClientRect();
                    dragBoxRef.current = { w: r.width, h: r.height };
                    if (pr && pr.width > 0 && pr.height > 0) dragOffsetRef.current = { dx: (e.clientX - pr.left) / pr.width - logo.x, dy: (e.clientY - pr.top) / pr.height - logo.y };
                    draggingLogoRef.current = true;
                    setActiveId(logo.id); setDraggingId(logo.id);
                  }}
                  className={`absolute ${activeId === logo.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''} ${draggingId === logo.id ? 'cursor-grabbing' : 'cursor-grab'}`}
                  style={{ left: `${logo.x * 100}%`, top: `${logo.y * 100}%`, width: `${logo.widthFrac * 100}%`, height: 'auto', opacity: logo.opacity, filter: logoFilter(logo.tint) }} />
              ))}
            </div>
          )}

        </main>

        {/* RIGHT — permanent 2-column card: narrow layer names | wide properties.
            Float mode: floating gradient card hugging the artboard's right edge. */}
        <aside
          className={floatMode
            ? 'hidden md:flex absolute z-30 rounded-2xl p-0.5 bg-gradient-to-br from-primary via-accent to-sky-400 shadow-[0_26px_60px_-15px_rgba(15,15,30,0.45)]'
            : 'hidden md:flex w-[372px] flex-shrink-0 bg-white border-l border-surface-dark/40'}
          style={floatMode ? { width: `${FLOAT_RIGHT_W}px`, left: `calc(50% + ${boxW / 2 + PANEL_GAP}px)`, top: `${FLOAT_TOP}px`, height: `${boxH}px` } : undefined}>
          <div className={`flex w-full ${floatMode ? 'rounded-[14px] bg-white overflow-hidden' : ''}`}>
            <div className="w-32 flex-shrink-0 border-r border-surface-dark/40 overflow-y-auto no-scrollbar">
              {layerList}
            </div>
            <div className="flex-1 min-w-0 overflow-y-auto no-scrollbar p-3">
              {propsPanel}
            </div>
          </div>
        </aside>
      </div>

      {/* ── MOBILE bottom sheet (Tools | Layers | Properties) ── */}
      <div className="md:hidden flex-shrink-0 border-t border-surface-dark/40 bg-white max-h-[46vh] flex flex-col">
        <div className="flex p-1 gap-1">
          {(['tools', 'layers', 'props'] as const).map((t) => (
            <button key={t} onClick={() => setMobileTab(t)}
              className={`flex-1 h-9 rounded-lg text-xs font-bold uppercase tracking-wide cursor-pointer ${mobileTab === t ? 'bg-primary text-white' : 'text-dark-muted'}`}>
              {t === 'tools' ? 'Tools' : t === 'layers' ? 'Layers' : 'Properties'}
            </button>
          ))}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          {mobileTab === 'tools' ? leftTools : mobileTab === 'layers' ? layerList : <div className="p-3">{propsPanel}</div>}
        </div>
      </div>

      {/* Publish */}
      {publishOpen && (
        <PostToSocialModal
          post={post}
          initialEnabled={['facebook', 'instagram']}
          headerLabel="Post or schedule"
          onClose={() => setPublishOpen(false)}
          onSuccess={(channels, mode) => {
            const names = channels.map((c) => c === 'instagram' ? 'Instagram' : c === 'facebook' ? 'Facebook' : c === 'tiktok' ? 'TikTok' : 'X').join(' + ');
            setToast(`${mode === 'now' ? 'Posted to' : 'Scheduled for'} ${names}`);
            setPublishOpen(false);
          }}
        />
      )}

      {/* Fullscreen preview */}
      {fullscreenUrl && (
        <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out" onClick={closeFullscreen}>
          <img src={fullscreenUrl} alt="Full screen preview" className="max-w-[96vw] max-h-[96vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button onClick={closeFullscreen} title="Close" className="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-4 py-2.5 rounded-full bg-dark text-white text-sm font-bold shadow-xl"
          onAnimationEnd={() => setToast(null)}>
          <button onClick={() => setToast(null)} className="cursor-pointer">{toast}</button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Floating toolbar — AI edit + Marquee + Re-render, docked bottom-center over
// the canvas. Stationary (doesn't scroll with the property rail).
// ═══════════════════════════════════════════════════════════════════════════
function LeftTools(props: {
  aiPrompt: string; setAiPrompt: (v: string) => void; onAiEdit: () => void; aiBusy: boolean;
  marqueeMode: boolean; onToggleMarquee: () => void;
  retryQ: 'low' | 'medium' | 'high' | null; onReRender: (q: 'low' | 'medium' | 'high') => void;
  mutating: boolean; opError: string | null;
  onPost: () => void; onDownload: () => void; downloading: boolean;
  onSave: () => void; saving: boolean; dirty: boolean;
}) {
  const { aiPrompt, setAiPrompt, onAiEdit, aiBusy, marqueeMode, onToggleMarquee, retryQ, onReRender, mutating, opError, onPost, onDownload, downloading, onSave, saving, dirty } = props;
  const [rrOpen, setRrOpen] = useState(false);
  const fileBtn = 'w-full h-10 px-3 inline-flex items-center gap-2 rounded-lg bg-surface text-dark hover:bg-surface-dark/30 text-xs font-bold uppercase tracking-wide cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 flex flex-col gap-3 p-3 overflow-y-auto no-scrollbar">
        {/* Describe an edit */}
        <div>
          <p className={sectionLabel}>Describe an edit</p>
          <div className="mt-2 rounded-xl p-0.5 bg-gradient-to-br from-primary via-accent to-sky-400">
            <div className="rounded-[10px] bg-white p-2 flex flex-col gap-2">
              <div className="flex items-start gap-2 px-2 pt-1.5 rounded-lg ring-1 ring-surface-dark/60 focus-within:ring-2 focus-within:ring-primary/60 transition-shadow">
                <i className="ri-sparkling-2-fill text-primary text-base mt-1 animate-pulse" />
                <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} disabled={mutating} rows={2}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && aiPrompt.trim()) { e.preventDefault(); onAiEdit(); } }}
                  placeholder="e.g. make the background a sunset"
                  className="flex-1 min-w-0 resize-none bg-transparent text-sm text-dark caret-primary focus:outline-none placeholder:text-dark-muted/80 disabled:opacity-50 py-1" />
              </div>
              <button onClick={onAiEdit} disabled={mutating || !aiPrompt.trim()}
                className="w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-primary/25">
                {aiBusy ? <><i className="ri-loader-4-line animate-spin" /> Editing…</> : <><i className="ri-magic-line" /> Apply · 1 cr</>}
              </button>
            </div>
          </div>
        </div>

        {/* Tools */}
        <div className="flex flex-col gap-2">
          <p className={sectionLabel}>Tools</p>
          <button onClick={onToggleMarquee} disabled={mutating} title="Edit a region"
            className={`h-10 px-3 inline-flex items-center gap-2 rounded-lg text-xs font-bold uppercase tracking-wide cursor-pointer disabled:opacity-40 ${marqueeMode ? 'bg-primary-light text-primary ring-1 ring-primary/50' : 'bg-surface text-dark hover:bg-surface-dark/30'}`}>
            <i className="ri-crop-line" /> Marquee
          </button>
          <div className="relative">
            <button onClick={() => setRrOpen((o) => !o)} disabled={mutating} title="Re-render at a quality"
              className="w-full h-10 px-3 inline-flex items-center gap-2 rounded-lg bg-surface text-dark hover:bg-surface-dark/30 text-xs font-bold uppercase tracking-wide cursor-pointer disabled:opacity-40">
              {retryQ ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-refresh-line" />}
              Re-render <i className="ri-arrow-down-s-line ml-auto" />
            </button>
            {rrOpen && !mutating && (
              <div className="absolute top-full left-0 mt-1 w-full z-30 rounded-xl bg-white ring-1 ring-surface-dark/40 shadow-xl p-1">
                {(['low', 'medium', 'high'] as const).map((q) => (
                  <button key={q} onClick={() => { setRrOpen(false); onReRender(q); }}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs font-bold uppercase text-dark hover:bg-surface cursor-pointer">
                    {q} · {q === 'low' ? '1' : q === 'medium' ? '2' : '4'} cr
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {opError && <p className="text-xs text-red-600 bg-red-50 rounded-lg py-1.5 px-2">{opError}</p>}
      </div>

      {/* File actions — bottom-docked, divider on top */}
      <div className="mt-auto p-3 border-t border-surface-dark/40 flex flex-col gap-2">
        <button onClick={onPost} disabled={mutating} className={fileBtn}><i className="ri-send-plane-line" /> Post</button>
        <button onClick={onDownload} disabled={downloading || mutating} className={fileBtn}>
          {downloading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-download-2-line" />} Download
        </button>
        <button onClick={onSave} disabled={saving || mutating || !dirty}
          className="w-full h-10 px-3 inline-flex items-center gap-2 rounded-lg bg-primary text-white hover:bg-primary-hover text-xs font-bold uppercase tracking-wide cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />} Save{dirty ? '' : 'd'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Layer list — narrow column 1 of the right rail (names + add). No file actions.
// ═══════════════════════════════════════════════════════════════════════════
function LayerList(props: {
  logoLayer: LogoOverlay | null; ctaLayer: TextOverlay | null; textLayers: TextOverlay[];
  activeId: string | null; setActiveId: (id: string | null) => void;
  addLogoLayer: () => void; addCtaLayer: () => void; addTextLayer: () => void;
  brandLogoUrl: string | null; onEditBakedText: () => void; hasBakedText: boolean;
}) {
  const { logoLayer, ctaLayer, textLayers, activeId, setActiveId, addLogoLayer, addCtaLayer, addTextLayer, brandLogoUrl, onEditBakedText, hasBakedText } = props;
  const Row = ({ id, label, icon }: { id: string; label: string; icon: string }) => (
    <button onClick={() => setActiveId(id)}
      className={`w-full flex items-center gap-1.5 h-9 px-2 rounded-lg cursor-pointer text-left ${activeId === id ? 'bg-primary-light ring-1 ring-primary/50' : 'hover:bg-surface-dark/30'}`}>
      <i className={`${icon} shrink-0 ${activeId === id ? 'text-primary' : 'text-dark-muted'}`} />
      <span className={`flex-1 min-w-0 truncate text-xs font-bold ${activeId === id ? 'text-primary' : 'text-dark'}`}>{label}</span>
    </button>
  );
  const addBtn = 'w-full flex items-center gap-1.5 h-9 px-2 rounded-lg text-dark-muted hover:text-primary hover:bg-surface-dark/20 text-xs font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex flex-col gap-0.5 p-2">
      <p className={`${sectionLabel} px-1 mb-1`}>Layers</p>
      {logoLayer ? <Row id={logoLayer.id} label="Logo" icon="ri-image-line" /> : (
        <button className={addBtn} onClick={addLogoLayer} disabled={!brandLogoUrl} title={brandLogoUrl ? '' : 'Set a brand logo first'}><i className="ri-add-line" /> Logo</button>
      )}
      {ctaLayer ? <Row id={ctaLayer.id} label={ctaLayer.text || 'CTA'} icon="ri-cursor-line" /> : (
        <button className={addBtn} onClick={addCtaLayer}><i className="ri-add-line" /> CTA</button>
      )}
      {textLayers.map((t) => <Row key={t.id} id={t.id} label={t.text || 'Text'} icon="ri-text" />)}
      <button className={addBtn} onClick={addTextLayer}><i className="ri-add-line" /> Text</button>
      <button onClick={onEditBakedText} disabled={!hasBakedText}
        title={hasBakedText ? '' : 'This image has no baked-in text to edit'}
        className="mt-1 w-full flex items-center gap-1.5 h-9 px-2 rounded-lg border border-surface-dark/40 text-dark-muted hover:border-primary/50 hover:text-primary text-[11px] font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
        <i className="ri-text shrink-0" /> Edit baked text · 2cr
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Property panel — the selected layer's properties (scrolls independently)
// ═══════════════════════════════════════════════════════════════════════════
function PropertyPanel(props: {
  activeOverlay: TextOverlay | null; activeLogo: LogoOverlay | null; brandColors: string[];
  updateOverlay: (id: string, u: Partial<TextOverlay>) => void;
  updateLogo: (id: string, u: Partial<LogoOverlay>) => void;
  removeLayer: (id: string) => void;
  placeTextAt: (posId: string) => void; placeLogoAt: (posId: string) => void;
}) {
  const { activeOverlay: o, activeLogo: lg, brandColors, updateOverlay, updateLogo, removeLayer, placeTextAt, placeLogoAt } = props;

  if (!o && !lg) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <i className="ri-cursor-line text-2xl text-dark-muted/40" />
        <p className="text-xs text-dark-muted max-w-[180px]">Select a layer — or add one — then drag it on the canvas.</p>
      </div>
    );
  }

  const Grid = ({ onPick }: { onPick: (p: string) => void }) => (
    <div className="grid grid-cols-3 gap-1 w-max">
      {PLACEMENT_CELLS.map((c) => (
        <button key={c} onClick={() => onPick(c)} title={c}
          className="w-7 h-7 rounded bg-surface hover:bg-primary flex items-center justify-center cursor-pointer group">
          <span className="w-1.5 h-1.5 rounded-full bg-dark-muted group-hover:bg-white" />
        </button>
      ))}
    </div>
  );
  const deleteBtn = (id: string) => (
    <button onClick={() => removeLayer(id)} className="mt-2 h-9 rounded-lg bg-red-50 text-red-600 text-xs font-bold uppercase cursor-pointer hover:bg-red-100"><i className="ri-delete-bin-line mr-1" /> Delete layer</button>
  );

  if (lg) {
    return (
      <div className="flex flex-col gap-3">
        <p className={sectionLabel}>Logo</p>
        <label className="text-[11px] text-dark-muted">Size — {Math.round(lg.widthFrac * 100)}%</label>
        <input type="range" min={5} max={50} value={Math.round(lg.widthFrac * 100)} onChange={(e) => updateLogo(lg.id, { widthFrac: Number(e.target.value) / 100 })} className="w-full accent-primary" />
        <label className="text-[11px] text-dark-muted">Opacity — {Math.round(lg.opacity * 100)}%</label>
        <input type="range" min={20} max={100} value={Math.round(lg.opacity * 100)} onChange={(e) => updateLogo(lg.id, { opacity: Number(e.target.value) / 100 })} className="w-full accent-primary" />
        <p className={sectionLabel}>Tint</p>
        <div className="flex gap-2">{(['original', 'black', 'white'] as LogoTint[]).map((t) => <button key={t} onClick={() => updateLogo(lg.id, { tint: t })} className={chip(lg.tint === t)}>{t}</button>)}</div>
        <p className={sectionLabel}>Placement</p>
        <Grid onPick={placeLogoAt} />
        {deleteBtn(lg.id)}
      </div>
    );
  }

  if (!o) return null;
  return (
    <div className="flex flex-col gap-3">
      <p className={sectionLabel}>{o.id.startsWith('cta-') ? 'CTA' : 'Text'}</p>
      <textarea value={o.text} onChange={(e) => updateOverlay(o.id, { text: e.target.value })} rows={2}
        className="w-full px-3 py-2 rounded-lg bg-white border border-surface-dark/40 focus:border-primary focus:outline-none text-sm text-dark resize-none" />
      <p className={sectionLabel}>Font</p>
      <select value={o.fontFamily} onChange={(e) => updateOverlay(o.id, { fontFamily: e.target.value })}
        className="w-full h-9 px-2 rounded-lg bg-white border border-surface-dark/40 text-dark text-sm focus:border-primary focus:outline-none">
        {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
      <label className="text-[11px] text-dark-muted">Size — {o.fontSize}</label>
      <input type="range" min={20} max={160} value={o.fontSize} onChange={(e) => updateOverlay(o.id, { fontSize: Number(e.target.value) })} className="w-full accent-primary" />
      <div className="flex gap-2 flex-wrap">
        {WEIGHT_OPTIONS.map((w) => <button key={w.l} onClick={() => updateOverlay(o.id, { fontWeight: w.v })} className={chip(o.fontWeight === w.v)}>{w.l}</button>)}
        {CASE_OPTIONS.map((c) => <button key={c.l} onClick={() => updateOverlay(o.id, { textTransform: c.v })} className={chip(o.textTransform === c.v)}>{c.l}</button>)}
      </div>
      <div className="flex gap-2">{ALIGN_OPTIONS.map((a) => <button key={a.v} onClick={() => updateOverlay(o.id, { textAlign: a.v })} className={chip(o.textAlign === a.v)}><i className={a.icon} /></button>)}</div>
      <p className={sectionLabel}>Color</p>
      <div className="flex items-center gap-2 flex-wrap">
        {[...BW_SWATCHES, ...brandColors].map((c) => (
          <button key={c} onClick={() => updateOverlay(o.id, { color: c })} title={c}
            className={`w-7 h-7 rounded-full border-2 cursor-pointer ${o.color?.toLowerCase() === c.toLowerCase() ? 'border-primary' : 'border-surface-dark/40'}`}
            style={{ backgroundColor: c }} />
        ))}
        <input type="color" value={o.color || '#ffffff'} onChange={(e) => updateOverlay(o.id, { color: e.target.value })} className="w-7 h-7 rounded cursor-pointer bg-transparent border border-surface-dark/40" />
      </div>
      <p className={sectionLabel}>Placement</p>
      <Grid onPick={placeTextAt} />
      {deleteBtn(o.id)}
    </div>
  );
}
